import {
  PushDestination,
  PushServiceType,
  PushEventType,
  CreatePushDestinationRequest,
  UpdatePushDestinationRequest,
  EmailDestinationConfig,
} from '../../shared/interfaces/push-destination.interface';

/**
 * Pure form ⇄ API mapping for the push-destination dialog.
 *
 * Extracts the parse/build helpers from the dialog component so the
 * email-config (de)serialization, recipient parsing, and create/update request
 * shaping can be unit-tested without a TestBed. Everything here is pure (no
 * Angular, no HTTP); the dialog component owns the FormGroup and passes its raw
 * value in.
 *
 * Mirrors the connection-form mapper pattern (SCRUM-760).
 */

/** A raw FormGroup value — untyped, so accessed via bracket notation. */
export type FormValue = Record<string, unknown>;

export interface ServiceTypeOption {
  value: PushServiceType;
  label: string;
  icon: string;
  hint: string;
}

/** The service-type picker cards. `email` is gated by deployment capabilities. */
export const PUSH_SERVICE_TYPES: ServiceTypeOption[] = [
  { value: 'slack', label: 'Slack', icon: 'tag', hint: 'Paste your Slack Incoming Webhook URL' },
  { value: 'teams', label: 'Teams', icon: 'groups', hint: 'Paste your Teams Incoming Webhook URL' },
  { value: 'zapier', label: 'Zapier', icon: 'bolt', hint: 'Paste your Zapier Webhook URL' },
  { value: 'generic', label: 'Generic', icon: 'webhook', hint: 'Paste your webhook endpoint URL (HTTPS required)' },
  { value: 'email', label: 'Email', icon: 'email', hint: 'Deliver notifications to one or more email addresses' },
];

/** The event checkboxes, in display order. */
export const PUSH_EVENT_TYPES: { value: PushEventType; label: string }[] = [
  { value: 'JobCompleted', label: 'Job Completed' },
  { value: 'JobFailed', label: 'Job Failed' },
  { value: 'JobNeedsReview', label: 'Job Needs Review' },
  { value: 'Manual', label: 'Manual (script notifications)' },
];

/** Split a free-text recipient field into individual addresses. */
export function parseAddresses(raw: unknown): string[] {
  return (typeof raw === 'string' ? raw : '')
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Lightweight, backtracking-free email shape check (the server validates
 * strictly). Avoids a regex to sidestep super-linear backtracking (S5852):
 * exactly one `@` (not at the ends), no whitespace, and a dot in the domain
 * that isn't at either end.
 */
export function looksLikeEmail(addr: string): boolean {
  if (addr.length === 0 || /\s/.test(addr)) return false;
  const at = addr.indexOf('@');
  if (at <= 0 || at !== addr.lastIndexOf('@') || at === addr.length - 1) {
    return false;
  }
  const domain = addr.slice(at + 1);
  const dot = domain.indexOf('.');
  return dot > 0 && dot < domain.length - 1;
}

/** Parse the JSON email config persisted in the `url` column. Tolerant of malformed data. */
export function parseEmailConfig(url: string): EmailDestinationConfig {
  try {
    const parsed = JSON.parse(url) as Partial<EmailDestinationConfig>;
    return {
      to: Array.isArray(parsed.to)
        ? parsed.to.filter((x): x is string => typeof x === 'string')
        : [],
      reply_to: typeof parsed.reply_to === 'string' ? parsed.reply_to : undefined,
    };
  } catch {
    return { to: [] };
  }
}

/** The selected service type from a raw form value. */
function serviceTypeOf(form: FormValue): PushServiceType {
  return form['serviceType'] as PushServiceType;
}

/**
 * The `url` column value for the selected service type: a trimmed webhook URL,
 * or — for email — a JSON recipient config `{ to, reply_to? }`.
 */
export function buildUrlValue(form: FormValue): string {
  if (serviceTypeOf(form) === 'email') {
    const cfg: EmailDestinationConfig = {
      to: parseAddresses(form['emailTo']),
    };
    const replyTo = String(form['emailReplyTo'] ?? '').trim();
    if (replyTo) cfg.reply_to = replyTo;
    return JSON.stringify(cfg);
  }
  return String(form['url'] ?? '').trim();
}

/** The events checked in the form, in canonical order. */
export function selectedEventsFromForm(form: FormValue): PushEventType[] {
  return PUSH_EVENT_TYPES.filter((et) => form[`event_${et.value}`]).map(
    (et) => et.value,
  );
}

export function buildCreateRequest(form: FormValue): CreatePushDestinationRequest {
  const request: CreatePushDestinationRequest = {
    name: String(form['name'] ?? '').trim(),
    serviceType: serviceTypeOf(form),
    url: buildUrlValue(form),
    scope: form['scope'] as CreatePushDestinationRequest['scope'],
    events: selectedEventsFromForm(form),
  };

  const credential = String(form['credential'] ?? '').trim();
  if (credential && serviceTypeOf(form) === 'generic') {
    request.credential = credential;
  }
  return request;
}

export function buildUpdateRequest(form: FormValue): UpdatePushDestinationRequest {
  return {
    name: String(form['name'] ?? '').trim(),
    url: buildUrlValue(form),
    scope: form['scope'] as UpdatePushDestinationRequest['scope'],
    events: selectedEventsFromForm(form),
    enabled: Boolean(form['enabled']),
  };
}

/**
 * Values to patch into the form when editing an existing destination — the
 * shared fields, the type-specific recipient/url fields, and one flag per event.
 */
export function destinationToFormValues(dest: PushDestination): FormValue {
  const values: FormValue = {
    serviceType: dest.serviceType,
    name: dest.name,
    scope: dest.scope,
    enabled: dest.enabled,
  };

  if (dest.serviceType === 'email') {
    const cfg = parseEmailConfig(dest.url);
    values['emailTo'] = cfg.to.join(', ');
    values['emailReplyTo'] = cfg.reply_to ?? '';
  } else {
    values['url'] = dest.url;
  }

  for (const et of PUSH_EVENT_TYPES) {
    values[`event_${et.value}`] = dest.events.includes(et.value);
  }
  return values;
}
